'use client'

import { useState } from 'react'

/** Resultado de {@link useImageError}. */
export interface UseImageErrorResult {
  /** `true` una vez que la imagen ha disparado `onError` en el navegador. */
  readonly hasError: boolean
  /** Manejador a pasar como `onError` de `<Image>`/`<img>`. */
  readonly onError: () => void
}

/**
 * Rastrea si una imagen ha fallado al cargar en el cliente (404, red caída,
 * CORS...). `onError` solo existe en el navegador — no hay equivalente en
 * servidor — de ahí que cualquier componente que use este hook deba ser
 * Client Component.
 *
 * Reinicia el estado a `hasError: false` en cuanto `src` cambia, ajustando el
 * estado durante el render (sin `useEffect`) para que una URL nueva reintente
 * la carga en vez de quedar marcada como rota para siempre — p. ej. una card
 * reutilizada por `key` con otra imagen tras cambiar de página en un listado.
 */
export function useImageError(src: string): UseImageErrorResult {
  const [state, setState] = useState({ src, hasError: false })

  if (state.src !== src) {
    setState({ src, hasError: false })
  }

  return {
    hasError: state.hasError,
    onError: () => setState((prev) => ({ ...prev, hasError: true })),
  }
}
